/**
 * CalendarTour — Geführte Mini-Tour im Kalender nach Onboarding
 *
 * Zeigt eine fixierte Floating-Card (bottom-right) mit Schritt-für-Schritt-
 * Erklärungen zu den wichtigsten Kalender-Features.
 *
 * Hebt das aktuelle Ziel-Element mit einer violetten Ring-Animation hervor
 * (via data-tour Attribut, ersatzweise data-testid) und scrollt es in den
 * sichtbaren Bereich. Fehlt das Ziel, wird der Schritt übersprungen; fehlen alle
 * weiteren Ziele, endet die Tour regulär über handleDone (statt ohne Highlight
 * stehenzubleiben oder kommentarlos zu verschwinden).
 *
 * Zähler, Fortschritt und Punkte zeigen immer nur die Schritte, die auf dieser
 * Seite wirklich erreichbar sind — ein Kunde ohne Monatsplan sieht also keine
 * Punkte für Schritte, die es für ihn nicht gibt.
 *
 * Props:
 *   active         — ob Tour gerade aktiv ist
 *   hasBonusPosts  — ob 🦊-Bonus-Posts im Kalender sind (zeigt Bonus-Schritt)
 *   onClose        — Callback wenn Tour beendet/übersprungen wird
 */

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, X, CheckCircle, Map } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/trackEvent";
import { getVariant } from "../lib/ab_utils";

// ── Tour-Schritte ──────────────────────────────────────────────────────────────
// target: data-tour value des Ziel-Elements (oder null für keinen Highlight).
// Die Namen sind absichtlich identisch zu den data-testids des Monatsplans —
// applyHighlight findet beides (siehe unten).

// Der erste Schritt hat bewusst KEIN Ziel-Element: direkt nach dem Onboarding
// existiert noch kein Monatsplan — und damit auch keines der Panel-Elemente.
// Ein Begrüßungsschritt ohne Highlight stimmt in beiden Fällen (mit und ohne Plan).
const BASE_STEPS = [
  {
    title: "Willkommen in deinem Kalender 🦊",
    desc: "Hier läuft alles zusammen: Förderfuchs stellt deine Beiträge für den Monat zusammen — mit Datum, Uhrzeit und Kanal. Kurz durchklicken, dann kennst du dich aus.",
    target: null,
  },
  {
    title: "Dein Monatsplan auf einen Blick",
    desc: "Sobald dein Plan steht, findest du hier alle vorbereiteten Beiträge als Tagesliste — von oben nach unten in der Reihenfolge, in der sie erscheinen.",
    target: "prepared-posts-panel",
  },
  {
    title: "Datum anklicken = Vorschau",
    desc: "Ein Klick auf das Datum eines Beitrags öffnet genau diesen Beitrag in der Vorschau — so, wie er später erscheint.",
    target: "prepared-post-date",
  },
  {
    title: "Eigenen Beitrag hinzufügen",
    desc: "Über das + ergänzt du an jedem Tag einen eigenen Beitrag — mit deinem Text und deinem Bild. Das war's!",
    target: "prepared-post-add",
  },
];

const BONUS_STEP = {
  title: "Deine Förderfuchs-Bonus-Posts 🦊",
  desc: "Violette Kacheln mit 🦊 sind deine Extra-Bonus-Posts. Du kannst sie wie jeden anderen Beitrag bearbeiten, verbessern oder löschen.",
  target: null,
};

// ── Tour-Highlight via DOM ────────────────────────────────────────────────────
// Steht das Ziel eines Schrittes gerade im DOM? Schritte ohne target zählen
// immer als vorhanden (sie brauchen kein Element).
// data-tour hat Vorrang; data-testid als Ersatz, damit die Tour auch ohne
// zusätzliches Attribut am Monatsplan-Panel greift.
function targetExists(targetKey) {
  if (!targetKey) return true;
  return !!(
    document.querySelector(`[data-tour="${targetKey}"]`) ||
    document.querySelector(`[data-testid="${targetKey}"]`)
  );
}

// Rückgabe: true, wenn ein Ziel-Element gefunden wurde (sonst kann der Schritt
// übersprungen werden, statt auf ein unsichtbares Ziel zu zeigen).
function applyHighlight(targetKey) {
  // Alle alten Highlights entfernen
  document.querySelectorAll(".ff-tour-highlight").forEach(el => {
    el.classList.remove("ff-tour-highlight");
  });

  if (!targetKey) return true;

  // data-tour hat Vorrang; data-testid als Ersatz, damit die Tour auch ohne
  // zusätzliches Attribut am Monatsplan-Panel greift.
  const el =
    document.querySelector(`[data-tour="${targetKey}"]`) ||
    document.querySelector(`[data-testid="${targetKey}"]`);
  if (!el) return false;

  el.classList.add("ff-tour-highlight");
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  return true;
}

// Nächster Schritt ab fromIdx, dessen Ziel wirklich im DOM steht. Findet sich
// keiner mehr, kommt -1 zurück → die Tour ist am Ende dessen, was zeigbar ist.
function findNextVisibleStep(steps, fromIdx) {
  for (let i = fromIdx; i < steps.length; i++) {
    if (targetExists(steps[i].target)) return i;
  }
  return -1;
}

// Indizes aller Schritte, die gerade wirklich zeigbar sind — die Grundlage für
// Zähler, Fortschritt und Punkte. Der aktuell angezeigte Schritt bleibt immer
// dabei (er steht ja gerade auf der Karte).
function visibleStepIndexes(steps, currentIdx) {
  const out = [];
  for (let i = 0; i < steps.length; i++) {
    if (i === currentIdx || targetExists(steps[i].target)) out.push(i);
  }
  return out;
}

function sameIndexes(a, b) {
  return !!a && a.length === b.length && a.every((v, i) => v === b[i]);
}

function clearHighlights() {
  document.querySelectorAll(".ff-tour-highlight").forEach(el => {
    el.classList.remove("ff-tour-highlight");
  });
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function CalendarTour({ active, hasBonusPosts, onClose }) {
  const navigate = useNavigate();
  const steps    = useMemo(
    () => (hasBonusPosts ? [...BASE_STEPS, BONUS_STEP] : BASE_STEPS),
    [hasBonusPosts]
  );

  const [stepIdx, setStepIdx] = useState(0);
  const current = steps[stepIdx] ?? steps[0];

  // Erreichbare Schritte (Indizes). null = noch nicht geprüft → bis zur ersten
  // Prüfung gelten alle Schritte als erreichbar. Bewusst erst NACH der
  // Nachlade-Wartezeit geprüft: ein Element kann schlicht noch nicht gerendert
  // sein (Plan lädt), dann dürfen wir Schritte nicht vorschnell wegwerfen.
  const [reachable, setReachable] = useState(null);

  const visibleIdx = useMemo(() => {
    const base = reachable
      ? reachable.filter(i => i < steps.length)
      : steps.map((_, i) => i);
    // Der aktuelle Schritt gehört immer dazu (sonst hätte die Karte keinen Punkt)
    return base.includes(stepIdx) ? base : [...base, stepIdx].sort((a, b) => a - b);
  }, [reachable, steps, stepIdx]);

  const pos   = visibleIdx.indexOf(stepIdx);   // Position im wirklich begehbaren Weg
  const total = visibleIdx.length;

  // AB-06: End-Button-Copy-Test
  const ab06Variant = useRef("A");
  useEffect(() => {
    if (active) {
      ab06Variant.current = getVariant("AB-06", { trackFn: trackEvent });
    }
  }, [active]);

  // reason: "finished"    — Kunde hat den begehbaren Weg durchgeklickt
  //         "no_targets"  — kein weiteres Ziel mehr vorhanden, die Tour ist am
  //                         Ende dessen, was es auf dieser Seite zu zeigen gibt.
  // In beiden Fällen ist die Tour regulär zu Ende (Event + Bonus-Teaser). Damit
  // das Abschluss-Event nichts Falsches behauptet, stehen Grund UND die wirklich
  // erreichbaren/gesehenen Schritte mit drin — „completed" heißt hier also nie
  // „der Kunde hat alle 4 bzw. 5 Schritte gesehen".
  const handleDone = useCallback((reason = "finished") => {
    clearHighlights();
    trackEvent("calendar_tour_completed", {
      variant: ab06Variant.current,
      steps_seen: (pos < 0 ? 0 : pos) + 1,
      steps_total: total,
      steps_defined: steps.length,
      reason,
    });
    onClose?.();
    // Bonus-Center-Teaser (dezenter Hinweis, kein Hard-Push)
    setTimeout(() => {
      toast(
        "💡 Tipp: Im Bonus-Center kannst du Freunde werben und Guthaben sammeln.",
        {
          duration: 7000,
          action: {
            label: "Bonus-Center",
            onClick: () => navigate("/bonus"),
          },
        }
      );
    }, 800);
  }, [onClose, navigate, pos, total, steps.length]);

  // Gleiches Muster wie onCloseRef: der Highlight-Effekt darf nicht an der
  // Identität von handleDone hängen (onClose ist ein Inline-Arrow und wechselt
  // bei jedem Eltern-Render) — sonst startet die 600-ms-Prüfung ständig neu.
  const handleDoneRef = useRef(handleDone);
  useEffect(() => { handleDoneRef.current = handleDone; }, [handleDone]);

  // Highlight-Effekt bei Schritt-Wechsel.
  // Fehlt das Ziel-Element (Panel noch nicht gerendert, Monat ohne Beiträge),
  // springt die Tour zum nächsten Schritt, dessen Ziel wirklich da ist, statt
  // auf ein Nichts zu zeigen. Der zweite Versuch nach kurzer Wartezeit fängt
  // nachladende Inhalte ab. Ist kein Ziel mehr übrig, endet die Tour — regulär
  // über handleDone, damit Abschluss-Event und Bonus-Teaser genauso greifen wie
  // bei einer durchgeklickten Tour.
  // Nach derselben Wartezeit steht auch fest, welche Schritte erreichbar sind —
  // erst dann werden Zähler/Punkte auf den wirklichen Weg gekürzt.
  useEffect(() => {
    if (!active) {
      clearHighlights();
      return;
    }
    const shown = applyHighlight(current.target);

    const t = setTimeout(() => {
      if (!shown && !applyHighlight(current.target)) {
        const next = findNextVisibleStep(steps, stepIdx + 1);
        if (next < 0) {
          handleDoneRef.current("no_targets");
          return;
        }
        setStepIdx(next);
        return;
      }
      setReachable(prev => {
        const next = visibleStepIndexes(steps, stepIdx);
        return sameIndexes(prev, next) ? prev : next;
      });
    }, 600);
    return () => clearTimeout(t);
  }, [active, stepIdx, current.target, steps]);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => clearHighlights();
  }, []);

  // Weiter geht es immer zum nächsten ERREICHBAREN Schritt — so landet niemand
  // auf einer Karte, die nur 600 ms lang auf ein fehlendes Element zeigt.
  const handleNext = useCallback(() => {
    const next = visibleIdx.find(i => i > stepIdx);
    if (next === undefined) {
      handleDone();
    } else {
      setStepIdx(next);
    }
  }, [stepIdx, visibleIdx, handleDone]);

  const handleSkip = useCallback(() => {
    clearHighlights();
    onClose?.();
  }, [onClose]);

  if (!active) return null;

  const isLast = pos === total - 1;

  // Ohne Monatsplan ist kein Schritt mit Ziel erreichbar — dann darf der
  // End-Button auch keinen „ersten Post" versprechen, den es nicht gibt.
  // (?. weil der Bonus-Schritt wegfallen kann, während er angezeigt wird)
  const hasPlanSteps = visibleIdx.some(i => steps[i]?.target);

  return (
    <>
      {/* Tour-Karte — fixiert bottom-right */}
      <div
        className="fixed bottom-6 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden"
        style={{ animation: "tourSlideIn 0.3s ease" }}
      >
        {/* Header-Strip */}
        <div className="bg-gradient-to-r from-violet-600 to-violet-500 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Map size={13} />
            <span className="text-xs font-semibold">Schnell-Tour</span>
            <span className="text-violet-300 text-xs">{pos + 1}/{total}</span>
          </div>
          <button
            onClick={handleSkip}
            className="text-violet-200 hover:text-white transition-colors"
            title="Tour beenden"
          >
            <X size={15} />
          </button>
        </div>

        {/* Fortschritts-Balken */}
        <div className="h-1 bg-stone-100">
          <div
            className="h-full bg-violet-400 transition-all duration-500"
            style={{ width: `${((pos + 1) / total) * 100}%` }}
          />
        </div>

        {/* Inhalt */}
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="font-semibold text-stone-900 text-sm leading-snug">{current.title}</p>
            <p className="text-stone-500 text-xs leading-relaxed mt-1.5">{current.desc}</p>
          </div>

          {/* Dot-Indikatoren — nur die wirklich erreichbaren Schritte, damit
              Anzahl und Position zum begehbaren Weg passen (und ein Klick nie
              auf einem übersprungenen, unsichtbaren Schritt landet). */}
          <div className="flex items-center gap-1">
            {visibleIdx.map(i => (
              <button
                key={i}
                onClick={() => setStepIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === stepIdx ? "bg-violet-500 w-4" : "bg-stone-300 hover:bg-stone-400"
                }`}
              />
            ))}
          </div>

          {/* Aktions-Buttons */}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={handleSkip}
              className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            >
              Tour beenden
            </button>
            <button
              onClick={() => {
                if (isLast) {
                  trackEvent("calendar_tour_cta_clicked", { variant: ab06Variant.current });
                }
                handleNext();
              }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
                isLast
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-violet-600 text-white hover:bg-violet-700"
              }`}
            >
              {isLast ? (
                <><CheckCircle size={13} /> {
                  !hasPlanSteps
                    ? "Alles klar 🦊"
                    : ab06Variant.current === "B" ? "Los geht's — Post öffnen" : "Ersten Post ansehen →"
                }</>
              ) : (
                <>Weiter <ChevronRight size={13} /></>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Globale CSS-Klasse für Tour-Highlight (inline-injected) */}
      <style>{`
        .ff-tour-highlight {
          outline: 2.5px solid #7c3aed !important;
          outline-offset: 3px;
          border-radius: 10px;
          animation: tourGlow 1.8s ease-in-out infinite;
          position: relative;
          z-index: 10;
        }
        @keyframes tourGlow {
          0%, 100% { outline-color: #7c3aed; box-shadow: 0 0 0 4px rgba(124,58,237,0.12); }
          50%       { outline-color: #a78bfa; box-shadow: 0 0 0 6px rgba(124,58,237,0.08); }
        }
        @keyframes tourSlideIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
